import { useEffect, useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';

const categories = ["All Products", "New Arrivals", "Best Sellers", "Trending"];

const PopularProducts = ({ setCartItemLength, fetchCart, cartButtons }) => {
    const [activeTab, setActiveTab] = useState("All Products");
    const navigate = useNavigate();
    const url = process.env.REACT_APP_API_URL;
    const userData = JSON.parse(localStorage.getItem("2xgxgshdugyywerw12gh")) || null; // Decrypt cookie data
    const isLoggedIn = !!userData?.user_id;
    const [variationModalData, setVariationModalData] = useState(null);
    const pageSize = 10;
    const [selectedGroupedProduct, setSelectedGroupedProduct] = useState(null);
    const [allProducts, setAllProducts] = useState([]);
    const [newArrivals, setNewArrivals] = useState([]);
    const [bestSellers, setBestSellers] = useState([]);
    const [trending, setTrending] = useState([]);
    const [showAllProducts, setShowAllProducts] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef();
    const [imgError, setImgError] = useState(false);

    const getDefaultCartButton = () => {
        const isGetQuoteDefault = cartButtons.find(b => b.button_id === 'getquote' && b.default);
        if (isGetQuoteDefault) return 'getquote';

        const isAddToCartDefault = cartButtons.find(b => b.button_id === 'addtocart' && b.default);
        if (isAddToCartDefault) return 'addtocart';

        const isBuyNowDefault = cartButtons.find(b => b.button_id === 'buynow' && b.default);
        if (isBuyNowDefault) return 'buynow';

        return null; // fallback
    };
    const defaultButton = getDefaultCartButton();

    const lastProductRef = useCallback(node => {
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prev => prev + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [hasMore]);

    const fetchAllProducts = async (page) => {
        try {
            const response = await axios.get(`${url}/grouped-product/search-all-products/${process.env.REACT_APP_PERFUME_ID}?page=${page}&limit=${pageSize}`);
            const data = response.data;

            if (data.length < pageSize) setHasMore(false);

            const parseSafe = (v) => {
                try {
                    return typeof v === "string" ? JSON.parse(v) : v || [];
                } catch {
                    return [];
                }
            };
            const activeProducts = data.filter(product => product.status === "Active");
            const cleaned = activeProducts.map(product => ({
                ...product,
                gallery: parseSafe(product.gallery),
                key_features: parseSafe(product.key_features),
                specifications: parseSafe(product.specifications),
                attribute: parseSafe(product.attribute),
                variation: parseSafe(product.variation),
            }));

            // De-duplicate by product_id
            setAllProducts(prev => {
                const ids = new Set(prev.map(p => p.product_id));
                const unique = cleaned.filter(p => !ids.has(p.product_id));
                return [...unique, ...prev];
            });

            setBestSellers(prev => {
                const ids = new Set(prev.map(p => p.product_id));
                const unique = cleaned.filter(
                    p => p.categorytype?.includes("Best Sellers") && !ids.has(p.product_id)
                );
                return [...unique, ...prev];
            });

            setTrending(prev => {
                const ids = new Set(prev.map(p => p.product_id));
                const unique = cleaned.filter(
                    p => p.categorytype?.includes("Trending") && !ids.has(p.product_id)
                );
                return [...unique, ...prev];
            });


        } catch (err) {
            console.error("Fetch error:", err);
            toast.error("Failed to load products");
        }
    };


    const fetchNewArrivals = async () => {
        try {
            const res = await axios.get(`${url}/newarrival/all/${process.env.REACT_APP_PERFUME_ID}`);
            if (res.data?.length > 0) {
                const category = res.data[0];
                const parsed = typeof category.product === "string"
                    ? JSON.parse(category.product)
                    : category.product;

                const parseSafe = (v) => {
                    try {
                        return typeof v === "string" ? JSON.parse(v) : v || [];
                    } catch {
                        return [];
                    }
                };

                const formatted = parsed.map(p => ({
                    ...p,
                    variation: parseSafe(p.variation),
                    gallery: parseSafe(p.gallery),
                    key_features: parseSafe(p.key_features),
                    specifications: parseSafe(p.specifications),
                    attribute: parseSafe(p.attribute),
                }));

                setNewArrivals(formatted);
            }
        } catch (err) {
            console.error("New Arrival fetch failed:", err);
            toast.error("Failed to load New Arrivals");
        }
    };

    useEffect(() => {
        fetchNewArrivals();
    }, []);

    useEffect(() => {
        fetchAllProducts(page);
    }, [page]);

    const getActiveProducts = () => {
        switch (activeTab) {
            case "All Products": return allProducts;
            case "New Arrivals": return newArrivals;
            case "Best Sellers": return bestSellers;
            case "Trending": return trending;
            default: return [];
        }
    };

    const handleProductClick = (id, name) => {
        // navigate(`/product/${name}/${id}`);
        const encryptedName = btoa(encodeURIComponent(name));
        const encryptedId = btoa(encodeURIComponent(id));

        navigate(`/product/${encryptedName}/${encryptedId}`);



    };

    const handleAddToCart = async (productId) => {
        const res = await fetch(`${url}/grouped-product/single/${productId}`);
        const latestProduct = await res.json();

        if (latestProduct.product_type === "Variable Product") {
            const safeVariation = typeof latestProduct.variation === "string"
                ? JSON.parse(latestProduct.variation)
                : latestProduct.variation || [];

            setVariationModalData({ ...latestProduct, variation: safeVariation });
        } else {
            addProductToCart(latestProduct, latestProduct);
        }

    };


    const syncCartItemsWithLatestProducts = async () => {
        const cartItems = JSON.parse(localStorage.getItem('guest_cart')) || [];

        const updatedCart = await Promise.all(cartItems.map(async (item) => {
            try {
                const res = await fetch(`${url}/grouped-product/single/${item.product_id}`);
                const latest = await res.json();

                return {
                    ...item,
                    product_id: latest.product_id,
                    product_name: latest.title,
                    product_price: latest.originalPrice,
                    product_saleprice: latest.discountPrice,
                    product_stockstatus: latest.stock,
                    product_status: latest.status,
                    product_image: latest.image,
                    product_gallery: latest.gallery || [],
                };
            } catch (e) {
                console.error("Sync error:", e);
                return item;
            }
        }));

        localStorage.setItem('guest_cart', JSON.stringify(updatedCart));
    };

    const addProductToCart = async (variation, baseProduct = null) => {
        const item = {
            product_id: variation.product_id || baseProduct?.product_id,
            product_sku: variation.sku || baseProduct?.sku,
            product_name: baseProduct?.title || variation.title,
            product_price: variation.price || baseProduct?.originalPrice,
            product_saleprice: variation.salePrice || baseProduct?.discountPrice,
            product_stockstatus: variation.stock || baseProduct?.stock,
            product_status: baseProduct?.status || 'Active',
            product_image: variation.imageUrl || baseProduct?.image,
            product_gallery: baseProduct?.gallery || [],
            description: baseProduct?.description || "",
            short_description: baseProduct?.short_description || "",
            category: baseProduct?.category || "",
            product_type: baseProduct.product_type,
            variation_attributes: variation.attributes || {},
            typeofsimple: '',
            user_id: userData?.user_id || null,
            user_name: userData?.name || null,
            delivery_days: baseProduct.delivery_days || "",
            main_id: process.env.REACT_APP_PERFUME_ID,
        };

        if (isLoggedIn) {
            try {
                const response = await fetch(`${url}/cart`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(item),
                });

                const result = await response.json();

                if (!response.ok || result.isError) {
                    toast.warn(result.msg || "Product already in cart");
                    return false;
                } else {
                    await fetchCart(userData?.user_id);
                    toast.success("Added to cart");
                    return true;
                }
            } catch (error) {
                console.error("Add to cart error:", error);
                toast.error("Something went wrong");
            }
        } else {
            const existing = JSON.parse(localStorage.getItem('guest_cart')) || [];
            if (existing.some(p => p.product_id === item.product_id)) {
                toast.warn("Product already in cart");
                return false;
            }
            const updatedCart = [...existing, item];
            localStorage.setItem('guest_cart', JSON.stringify(updatedCart));
            toast.success("Added to cart");
            setCartItemLength(updatedCart.length);
            return true;
        }
    };

    const handleBuyNow = async (productId) => {
        const res = await fetch(`${url}/grouped-product/single/${productId}`);
        const latestProduct = await res.json();

        if (latestProduct.product_type === "Variable Product") {
            const safeVariation = typeof latestProduct.variation === "string"
                ? JSON.parse(latestProduct.variation)
                : latestProduct.variation || [];

            setVariationModalData({ ...latestProduct, variation: safeVariation });
        } else {
            handleBuyCart(latestProduct, latestProduct);
        }

    };

    const handleBuyCart = (variation, baseProduct = null) => {
        const item = {
            product_id: variation.product_id || baseProduct?.product_id,
            product_sku: variation.sku || baseProduct?.sku,
            product_name: baseProduct?.title || variation.title,
            product_price: Number(variation.price) || baseProduct?.originalPrice,
            product_saleprice: Number(variation.salePrice) || baseProduct?.discountPrice,
            product_stockstatus: variation.stock || baseProduct?.stock,
            product_status: baseProduct?.status || 'Active',
            product_image: variation.imageUrl || baseProduct?.image,
            product_gallery: baseProduct?.gallery || [],
            description: baseProduct?.description || "",
            short_description: baseProduct?.short_description || "",
            category: baseProduct?.category || "",
            product_type: baseProduct.product_type,
            variation_attributes: variation.attributes || {},
            typeofsimple: '',
            user_id: userData?.user_id || null,
            user_name: userData?.name || null,
            delivery_days: baseProduct.delivery_days || "",
            main_id: process.env.REACT_APP_PERFUME_ID,
        };

        const products = [item]
        let discount = 0
        const totalSalePrice = products.reduce((acc, p) => acc + Number(p.product_saleprice), 0);
        const totalBeforeDiscount = products.reduce((acc, p) => acc + Number(p.product_price), 0);
        const totalAfterDiscount = (totalSalePrice - discount).toFixed(2);

        const checkoutDetails = {
            products,
            totalBeforeDiscount: totalBeforeDiscount,
            totalAfterDiscount,
        };

        localStorage.setItem("checkout_data", JSON.stringify(checkoutDetails));
        navigate("/checkout")
    }


    useEffect(() => {
        syncCartItemsWithLatestProducts();
    }, [])

    return (
        <div className="new-arrival">
            <motion.h2 className="section-title" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                Popular Products
            </motion.h2>
            <motion.p className="section-description" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}>
                Discover the latest trends with our stylish new collection.
            </motion.p>

            <div className="tabs">
                {categories.map(cat => (
                    <button key={cat} className={`tab-button ${activeTab === cat ? "active" : ""}`} onClick={() => setActiveTab(cat)}>
                        {cat}
                    </button>
                ))}
            </div>

            <div className="popupar-products">
                {getActiveProducts()
                    .slice(0, activeTab === "All Products" && !showAllProducts ? 20 : undefined)
                    .map((product, i) => (
                        <div className="product-new-card" key={product.product_id || product.id}>
                            <div className="product-new-img-wrapper">
                                {!imgError && product.image ? (
                                    <img
                                        src={`${url}${product.image}`}
                                        alt={product.title}
                                        className="product-new-img"
                                        onClick={() => handleProductClick(product.product_id, product.title)}
                                        onError={() => setImgError(true)}
                                    />
                                ) : (
                                    <Skeleton
                                        height={250}
                                        width="100%"
                                        style={{ borderRadius: '8px' }}
                                        onClick={() => handleProductClick(product.product_id, product.title)}
                                    />
                                )}
                                {(() => {
                                    const originalPrice = product.product_type === "Variable Product"
                                        ? Math.min(...(product.variation || []).map(v => Number(v.price)).filter(Boolean))
                                        : Number(product.originalPrice);

                                    const salePrice = product.product_type === "Variable Product"
                                        ? Math.min(...(product.variation || []).map(v => Number(v.salePrice || v.price)).filter(Boolean))
                                        : Number(product.discountPrice);

                                    if (originalPrice && salePrice && salePrice < originalPrice) {
                                        const percent = Math.round(((originalPrice - salePrice) / originalPrice) * 100);
                                        return (
                                            <span className="product-new-discount">
                                                {percent}% OFF
                                            </span>
                                        );
                                    }
                                    return null;
                                })()}

                            </div>

                            <div className="product-new-content">
                                <h3
                                    className="product-new-title"
                                    onClick={() => handleProductClick(product.product_id, product.title)}
                                >
                                    {product.title}
                                </h3>
                                <div className="product-new-price-box">
                                    {product.product_type === "Variable Product" ? (() => {
                                        const variationPrices = product.variation.map(v => Number(v.price)).filter(Boolean);
                                        const variationSalePrices = product.variation.map(v => Number(v.salePrice || v.price)).filter(Boolean);

                                        const hasPrice = variationPrices.length > 0;
                                        const hasSalePrice = variationSalePrices.length > 0;

                                        return (
                                            <>
                                                {hasSalePrice && (
                                                    <span className="product-new-price">
                                                        ₹{Math.min(...variationSalePrices)}
                                                    </span>
                                                )}
                                                {hasPrice && (
                                                    <span className="product-new-original-price">
                                                        <del>₹{Math.min(...variationPrices)}</del>
                                                    </span>
                                                )}
                                            </>
                                        );
                                    })() : (
                                        <>
                                            <span className="product-new-price">
                                                ₹{product.discountPrice || "N/A"}
                                            </span>
                                            {product.originalPrice && (
                                                <span className="product-new-original-price">
                                                    <del>₹{product.originalPrice}</del>
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>

                                <div className="product-new-actions">
                                    {product.product_type === "Grouped Product" ? (
                                        <>
                                            <button
                                                className="btn-view-product"
                                                onClick={() => setSelectedGroupedProduct(product)}
                                            >
                                                View Details
                                            </button>
                                            <button
                                                className="btn-add-cart"
                                                onClick={() => handleAddToCart(product.product_id)}
                                            >
                                                Package
                                            </button>
                                        </>
                                    ) : product.product_type === "Variable Product" ? (
                                        <button
                                            className="btn-add-cart"
                                            onClick={() => handleAddToCart(product.product_id)}
                                        >
                                            Select
                                        </button>
                                    ) : product.totalStock === 0 ? (
                                        <button className="btn-add-cart" disabled>
                                            Out of Stock
                                        </button>
                                    ) : (
                                        <>
                                            {defaultButton === "addtocart" && (
                                                <button
                                                    className="btn-add-cart"
                                                    onClick={() => handleAddToCart(product.product_id)}
                                                >
                                                    Add to Cart
                                                </button>
                                            )}
                                            {defaultButton === "buynow" && (
                                                <button
                                                    className="btn-add-cart"
                                                    onClick={() => handleBuyNow(product.product_id)}
                                                >
                                                    Buy Now
                                                </button>
                                            )}
                                            {defaultButton === "getquote" && (
                                                <button
                                                    className="btn-add-cart"
                                                    onClick={() => handleProductClick(product.product_id, product.title)}
                                                >
                                                    Get Quote
                                                </button>
                                            )}
                                        </>
                                    )}

                                    {/* Show View button only if NOT Grouped Product AND not getquote */}
                                    {product.product_type !== "Grouped Product" && defaultButton !== "getquote" && (
                                        <button
                                            className="btn-view-product"
                                            onClick={() => handleProductClick(product.product_id, product.title)}
                                        >
                                            View
                                        </button>
                                    )}
                                </div>

                            </div>
                        </div>
                    ))}

                {activeTab === "All Products" && !showAllProducts && allProducts.length > 20 && (
                    <div className="show-more-container">
                        <button className="show-more-button" onClick={() => navigate('./course')}>
                            Show More Products
                        </button>
                    </div>
                )}

            </div>
            {variationModalData && (
                <VariationModal
                    product={variationModalData}
                    onClose={() => setVariationModalData(null)}
                    cartButtons={cartButtons}
                    onAddToCart={async (variation) => {
                        const success = await addProductToCart(variation, variationModalData);
                        if (success) {
                            setVariationModalData(null);
                        }
                    }}
                    onAddToBuyNow={async (variation) => {
                        const success = await handleBuyCart(variation, variationModalData);
                        if (success) {
                            setVariationModalData(null); // ✅ Only close if added
                        }
                    }}
                />
            )}

            {selectedGroupedProduct && (() => {
                const groupedItems = selectedGroupedProduct.grouped_product
                    ? JSON.parse(selectedGroupedProduct.grouped_product)
                    : [];


                return (
                    <motion.div className="group-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <motion.div className="grouped-modal-content" initial={{ y: -50 }} animate={{ y: 0 }}>
                            <div className="group-modal-header">
                                <h2>{selectedGroupedProduct.title}</h2>
                                <button onClick={() => setSelectedGroupedProduct(null)} className="group-modal-close">
                                    <i className="fa fa-times" />
                                </button>
                            </div>

                            <div className="group-modal-body">
                                {groupedItems.length > 0 && (
                                    <>
                                        <div className="grouped-items-list">
                                            {groupedItems.map((item, idx) => (
                                                <div key={idx} className="grouped-item-card">
                                                    <img
                                                        src={`${url}${item.image}`}
                                                        alt={item.title}
                                                        className="grouped-item-image"
                                                    />
                                                    <div className="grouped-item-details">
                                                        <p className="grouped-item-title">{item.title}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                    </>
                                )}
                            </div>
                            {/* <p className='customization-text'>For any customization contact on whatsapp</p> */}
                            <div className="group-modal-footer">
                                <button
                                    className="grouped-product-btn-primary"
                                    onClick={() => {
                                        handleAddToCart(selectedGroupedProduct.product_id);
                                        setSelectedGroupedProduct(null);
                                    }}
                                >
                                    Select Package
                                </button>
                            </div>

                        </motion.div>
                    </motion.div >
                );
            })()}

        </div>
    );
};


const VariationModal = ({ product, onClose, onAddToCart, cartButtons, onAddToBuyNow }) => {
    const url = process.env.REACT_APP_API_URL

    const getDefaultCartButton = () => {
        const isGetQuoteDefault = cartButtons.find(b => b.button_id === 'getquote' && b.default);
        if (isGetQuoteDefault) return 'getquote';

        const isAddToCartDefault = cartButtons.find(b => b.button_id === 'addtocart' && b.default);
        if (isAddToCartDefault) return 'addtocart';

        const isBuyNowDefault = cartButtons.find(b => b.button_id === 'buynow' && b.default);
        if (isBuyNowDefault) return 'buynow';

        return null; // fallback
    };

    const defaultButton = getDefaultCartButton();

    return (
        <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <motion.div className="variation-modal" initial={{ y: -50 }} animate={{ y: 0 }}>
                <div className="modal-header">
                    <h2>{product.title} - Choose a Variation</h2>
                    <button className="modal-close" onClick={onClose}><i className="fa fa-times" /></button>
                </div>

                <div className="variation-flex-container">
                    {(Array.isArray(product.variation) ? product.variation : []).map((v, idx) => (
                        <div key={idx} className="variation-flex-card">
                            <img src={`${url}${v.imageUrl}`} alt={v.sku} />
                            <div className="variation-info">
                                <p><strong>SKU:</strong> {v.sku}</p>
                                {
                                    v.price > 0 && <p><strong>Price:</strong> ₹{v.price}</p>
                                }

                                {v.salePrice > 0 && <p><strong>Sale:</strong> ₹{v.salePrice}</p>}
                                <p><strong>Stock:</strong> {v.stock}</p>
                                {defaultButton === 'addtocart' && (
                                    <button onClick={() => onAddToCart(v)} className="add-cart-btn">
                                        Add to Cart
                                    </button>
                                )}
                                {defaultButton === 'buynow' && (
                                    <button onClick={() => onAddToBuyNow(v)} className="add-cart-btn">
                                        Buy Now
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </motion.div>
        </motion.div>
    );
};
export default PopularProducts;
